import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Sparkles, Download, FileSpreadsheet, FileText, Filter, TrendingUp, TrendingDown, IndianRupee, Landmark, Receipt, Scale, RefreshCw } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token') || localStorage.getItem('c24_admin_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const fmt = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;
const monthOptions = () => {
  const out = [{ label: 'All time', value: '' }];
  const seen = new Set();
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  for (let i = 0; i < 12; i++) {
    const d = new Date(year, month - i, 1);
    const y = d.getFullYear(); const m = d.getMonth() + 1;
    const value = `${y}-${String(m).padStart(2, '0')}`;
    if (seen.has(value)) continue;
    seen.add(value);
    out.push({ label: d.toLocaleString('en-IN', { month: 'long', year: 'numeric' }), value });
  }
  return out;
};

const AdminReports = () => (
  <AdminLayout title="Financial Reports">
    <ReportsPanel scopeRole="admin" />
  </AdminLayout>
);

export const ReportsPanel = ({ scopeRole = 'admin' }) => {
  const [reportKind, setReportKind] = useState('pnl'); // 'pnl' | 'bs'
  const [franchises, setFranchises] = useState([]);
  const [franchiseId, setFranchiseId] = useState('');
  const [period, setPeriod] = useState('');
  const [pnl, setPnl] = useState(null);
  const [bs, setBs] = useState(null);
  const [narrative, setNarrative] = useState('');
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null); // 'pdf' | 'xlsx' | null

  const loadFranchises = async () => {
    try {
      const { data } = await axios.get(`${API}/franchises`, authCfg());
      setFranchises(data || []);
    } catch (e) { console.warn(e); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setNarrative('');
    try {
      if (reportKind === 'pnl') {
        const p = new URLSearchParams();
        if (franchiseId) p.set('franchise_id', franchiseId);
        if (period) p.set('period', period);
        const { data } = await axios.get(`${API}/reports/pnl?${p.toString()}`, authCfg());
        setPnl(data);
      } else {
        const p = new URLSearchParams();
        if (franchiseId) p.set('franchise_id', franchiseId);
        const { data } = await axios.get(`${API}/reports/balance-sheet?${p.toString()}`, authCfg());
        setBs(data);
      }
    } catch (e) { console.warn(e); }
    setLoading(false);
  }, [reportKind, franchiseId, period]);

  useEffect(() => { loadFranchises(); }, []);
  useEffect(() => { load(); }, [load]);

  const generateNarrative = async () => {
    setNarrativeLoading(true);
    try {
      const { data } = await axios.post(`${API}/reports/pnl-narrative`, { franchise_id: franchiseId || null, period: period || null }, authCfg());
      setNarrative(data.narrative || '(No narrative generated — please try again in a moment.)');
    } catch (e) { setNarrative('(AI narrative unavailable at the moment.)'); }
    setNarrativeLoading(false);
  };

  const download = async (fmt_) => {
    setDownloading(fmt_);
    try {
      const p = new URLSearchParams();
      if (franchiseId) p.set('franchise_id', franchiseId);
      if (period && reportKind === 'pnl') p.set('period', period);
      if (reportKind === 'pnl' && narrative) p.set('include_narrative', 'true');
      const path = reportKind === 'pnl' ? `/reports/pnl/export.${fmt_}` : `/reports/balance-sheet/export.${fmt_}`;
      const { data } = await axios.get(`${API}${path}?${p.toString()}`, { ...authCfg(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportKind}-${franchiseId || 'consolidated'}-${period || 'alltime'}.${fmt_}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.warn(e); }
    setDownloading(null);
  };

  const scopeLabel = (() => {
    // Franchise-mode: derive label from single-franchise list returned by /api/franchises
    if (scopeRole === 'franchise') {
      const f = franchises[0];
      return f ? `${f.district} (${f.franchise_id})` : 'Your franchise';
    }
    if (franchiseId) return franchises.find((f) => f.franchise_id === franchiseId)?.district || franchiseId;
    return 'Consolidated (all franchises)';
  })();

  return (
    <>
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4 bg-white border border-neutral-200 rounded-xl p-3" data-testid="reports-filter-bar">
        <span className="text-xs font-semibold text-neutral-500 inline-flex items-center gap-1"><Filter className="w-3.5 h-3.5" /> Report:</span>
        <div className="flex gap-1 bg-neutral-100 rounded-lg p-1">
          <button data-testid="reports-kind-pnl" onClick={() => setReportKind('pnl')}
            className={`px-3 py-1 rounded-md text-xs font-semibold ${reportKind === 'pnl' ? 'bg-[#2E7D32] text-white' : 'text-neutral-600 hover:bg-white'}`}>P&L</button>
          <button data-testid="reports-kind-bs" onClick={() => setReportKind('bs')}
            className={`px-3 py-1 rounded-md text-xs font-semibold ${reportKind === 'bs' ? 'bg-[#7B1FA2] text-white' : 'text-neutral-600 hover:bg-white'}`}>Balance Sheet</button>
        </div>

        {scopeRole === 'admin' && (
          <>
            <span className="text-xs font-semibold text-neutral-500 ml-3">Scope:</span>
            <select data-testid="reports-franchise-filter" value={franchiseId} onChange={(e) => setFranchiseId(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-neutral-200 text-xs bg-white">
              <option value="">Consolidated (all franchises)</option>
              {franchises.map((f) => <option key={f.franchise_id} value={f.franchise_id}>{f.district} ({f.franchise_id})</option>)}
            </select>
          </>
        )}

        {reportKind === 'pnl' && (
          <>
            <span className="text-xs font-semibold text-neutral-500 ml-3">Period:</span>
            <select data-testid="reports-period-filter" value={period} onChange={(e) => setPeriod(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-neutral-200 text-xs bg-white">
              {monthOptions().map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </>
        )}

        <div className="flex gap-2 ml-auto">
          <button data-testid="reports-download-pdf" onClick={() => download('pdf')} disabled={loading || downloading}
            className="inline-flex items-center gap-1.5 bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 text-xs font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50">
            {downloading === 'pdf' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} PDF
          </button>
          <button data-testid="reports-download-xlsx" onClick={() => download('xlsx')} disabled={loading || downloading}
            className="inline-flex items-center gap-1.5 bg-green-50 hover:bg-green-100 border border-green-200 text-green-700 text-xs font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50">
            {downloading === 'xlsx' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />} Excel
          </button>
          <button data-testid="reports-refresh" onClick={load} disabled={loading} className="p-1.5 rounded-lg border border-neutral-200 bg-white hover:bg-neutral-50 disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 text-neutral-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* AI narrative */}
      {reportKind === 'pnl' && (
        <div className="bg-gradient-to-r from-[#F3E8FF] to-[#DBEAFE] border border-purple-200 rounded-2xl p-4 mb-4" data-testid="ai-narrative-panel">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-[#7B1FA2]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm text-[#4C1D95] flex items-center gap-2">AI Accounting Agent <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white text-purple-600">Claude Sonnet 4.5</span></p>
              {narrative ? (
                <p className="text-sm text-neutral-800 mt-1.5 leading-relaxed" data-testid="ai-narrative-text">{narrative}</p>
              ) : (
                <p className="text-xs text-neutral-500 mt-1">Get a 1-paragraph natural-language summary of this P&L with the top insight and one actionable recommendation.</p>
              )}
              {!narrative && (
                <button data-testid="ai-narrative-generate" onClick={generateNarrative} disabled={narrativeLoading}
                  className="mt-2 inline-flex items-center gap-1.5 bg-[#7B1FA2] hover:bg-[#6A1B9A] text-white text-xs font-semibold px-3 py-1.5 rounded-lg disabled:opacity-70">
                  {narrativeLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {narrativeLoading ? 'Generating…' : 'Generate AI Summary'}
                </button>
              )}
              {narrative && (
                <button data-testid="ai-narrative-regen" onClick={generateNarrative} disabled={narrativeLoading}
                  className="mt-2 text-[11px] font-semibold text-[#7B1FA2] hover:underline">Regenerate</button>
              )}
            </div>
          </div>
        </div>
      )}

      {loading && <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>}

      {!loading && reportKind === 'pnl' && pnl && (
        <PnLView pnl={pnl} scopeLabel={scopeLabel} />
      )}

      {!loading && reportKind === 'bs' && bs && (
        <BalanceSheetView bs={bs} scopeLabel={scopeLabel} />
      )}
    </>
  );
};

const Kpi = ({ label, value, sub, color, bg, icon: Icon, testid }) => (
  <div data-testid={testid} className="bg-white border border-neutral-200 rounded-xl p-4">
    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: bg }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <p className="mt-3 text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">{label}</p>
    <p className="font-extrabold text-lg mt-0.5" style={{ color }}>{value}</p>
    {sub && <p className="text-[10px] text-neutral-400 mt-0.5">{sub}</p>}
  </div>
);

const Row = ({ label, value, indent, bold, positive, negative }) => (
  <tr className={`border-b border-neutral-100 ${bold ? 'bg-neutral-50 font-bold' : ''}`}>
    <td className={`px-4 py-2 text-xs ${indent ? 'pl-10 text-neutral-600' : 'font-semibold'}`}>{label}</td>
    <td className={`px-4 py-2 text-right text-xs font-bold ${positive ? 'text-[#2E7D32]' : negative ? 'text-red-600' : ''}`}>{value}</td>
  </tr>
);

const PnLView = ({ pnl, scopeLabel }) => (
  <div className="grid md:grid-cols-3 gap-4" data-testid="pnl-view">
    <div className="md:col-span-1 space-y-3">
      <Kpi label="Total Revenue" value={fmt(pnl.revenue.total)} icon={TrendingUp} color="#2E7D32" bg="#E8F5E9" testid="pnl-kpi-revenue" />
      <Kpi label="Gross Profit"  value={fmt(pnl.gross_profit)} sub="Revenue − Cost of sales" icon={IndianRupee} color="#1E40AF" bg="#DBEAFE" testid="pnl-kpi-gross" />
      <Kpi label="Total Expenses" value={fmt(pnl.operating_expenses.total)} icon={TrendingDown} color="#C2410C" bg="#FEF3C7" testid="pnl-kpi-expenses" />
      <Kpi label="Net Profit" value={fmt(pnl.net_profit)} sub={pnl.net_profit >= 0 ? 'Profitable' : 'Loss'} icon={pnl.net_profit >= 0 ? TrendingUp : TrendingDown} color={pnl.net_profit >= 0 ? '#065F46' : '#991B1B'} bg={pnl.net_profit >= 0 ? '#D1FAE5' : '#FEE2E2'} testid="pnl-kpi-net" />
    </div>

    <div className="md:col-span-2 bg-white border border-neutral-200 rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50 flex items-center gap-2">
        <p className="font-bold text-sm flex-1">Profit & Loss — {scopeLabel}</p>
        <span className="text-[11px] text-neutral-500">{pnl.scope.period}</span>
      </div>
      <table className="w-full text-sm" data-testid="pnl-table">
        <thead className="border-b border-neutral-200"><tr><th className="px-4 py-2 text-[10px] font-bold text-neutral-500 uppercase text-left">Line item</th><th className="px-4 py-2 text-[10px] font-bold text-neutral-500 uppercase text-right">Amount</th></tr></thead>
        <tbody>
          <Row label="Revenue" />
          <Row label="Company revenue (HQ share)" value={fmt(pnl.revenue.company_revenue)} indent positive />
          <Row label="Franchise earnings" value={fmt(pnl.revenue.franchise_earnings)} indent positive />
          <Row label="Other income" value={fmt(pnl.revenue.other_income)} indent positive />
          <Row label="HQ recurring charges" value={fmt(pnl.revenue.hq_receivable)} indent positive />
          <Row label="Total revenue" value={fmt(pnl.revenue.total)} bold positive />

          <Row label="Cost of sales" />
          <Row label="Cleaner payouts" value={fmt(pnl.cost_of_sales.cleaner_payouts)} indent negative />
          <Row label="Total cost of sales" value={fmt(pnl.cost_of_sales.total)} bold negative />

          <Row label="Gross profit" value={fmt(pnl.gross_profit)} bold positive={pnl.gross_profit >= 0} negative={pnl.gross_profit < 0} />

          <Row label="Operating expenses" />
          {Object.entries(pnl.operating_expenses.by_category).map(([k, v]) => {
            const anom = pnl.operating_expenses.anomalies?.[k];
            const label = k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
            return (
              <tr key={k} data-testid={`pnl-exp-row-${k}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                <td className="px-4 py-2 text-xs text-neutral-600 pl-10">
                  {label}
                  {anom && (
                    <span data-testid={`pnl-anomaly-${k}`} className="ml-2 inline-flex items-center gap-1 bg-red-100 text-red-700 text-[10px] font-bold px-1.5 py-0.5 rounded" title={anom.reason === 'spike' ? `${anom.factor}× vs prior month (was ₹${anom.previous.toLocaleString('en-IN')})` : 'New expense line — no prior month data'}>
                      ⚠ {anom.reason === 'spike' ? `${anom.factor}× spike` : 'NEW'}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-right text-xs font-bold text-red-600">{fmt(v)}</td>
              </tr>
            );
          })}
          {Object.keys(pnl.operating_expenses.by_category).length === 0 && <Row label="(no operating expenses recorded)" indent />}
          <Row label="Total operating expenses" value={fmt(pnl.operating_expenses.total)} bold negative />

          <tr><td colSpan={2} className="border-t-2 border-neutral-300"></td></tr>
          <Row label="NET PROFIT" value={fmt(pnl.net_profit)} bold positive={pnl.net_profit >= 0} negative={pnl.net_profit < 0} />
        </tbody>
      </table>
      <div className="px-4 py-3 border-t border-neutral-100 text-[11px] text-neutral-500">
        GST collected — CGST {fmt(pnl.gst.cgst)} · SGST {fmt(pnl.gst.sgst)} · Total {fmt(pnl.gst.total)}
      </div>
    </div>
  </div>
);

const BalanceSheetView = ({ bs, scopeLabel }) => (
  <div className="grid md:grid-cols-3 gap-4" data-testid="bs-view">
    <div className="md:col-span-1 space-y-3">
      <Kpi label="Total Assets"       value={fmt(bs.assets.total)}       icon={Landmark} color="#1E88E5" bg="#DBEAFE" testid="bs-kpi-assets" />
      <Kpi label="Total Liabilities"  value={fmt(bs.liabilities.total)}  icon={Receipt}  color="#C2410C" bg="#FEF3C7" testid="bs-kpi-liab" />
      <Kpi label="Total Equity"       value={fmt(bs.equity.total)}       icon={Scale}    color="#7B1FA2" bg="#F3E5F5" testid="bs-kpi-equity" />
      <div data-testid="bs-balance-status" className={`p-3 rounded-xl border ${bs.balanced ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'} text-xs font-semibold`}>
        {bs.balanced ? '✓ Assets = Liabilities + Equity' : '⚠ Unbalanced — please investigate'}
      </div>
    </div>

    <div className="md:col-span-2 bg-white border border-neutral-200 rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50 flex items-center gap-2">
        <p className="font-bold text-sm flex-1">Balance Sheet — {scopeLabel}</p>
        <span className="text-[11px] text-neutral-500">As of {bs.scope.as_of.slice(0, 10)}</span>
      </div>
      <table className="w-full text-sm" data-testid="bs-table">
        <tbody>
          <Row label="Assets" />
          {bs.assets.current.bank_accounts.map((b) => (
            <Row key={b.account_id} label={`${b.name} (${b.type})`} value={fmt(b.balance)} indent positive />
          ))}
          {bs.assets.current.bank_accounts.length === 0 && <Row label="(no bank accounts)" indent />}
          <Row label="Total assets" value={fmt(bs.assets.total)} bold positive />

          <Row label="Liabilities" />
          <Row label="CGST payable" value={fmt(bs.liabilities.cgst_payable)} indent negative />
          <Row label="SGST payable" value={fmt(bs.liabilities.sgst_payable)} indent negative />
          <Row label="GST payable (legacy)" value={fmt(bs.liabilities.gst_payable_legacy)} indent negative />
          <Row label="Cleaner payable" value={fmt(bs.liabilities.cleaner_payable)} indent negative />
          <Row label="Total liabilities" value={fmt(bs.liabilities.total)} bold negative />

          <Row label="Equity" />
          <Row label="Retained earnings" value={fmt(bs.equity.retained_earnings)} indent />
          <Row label="Owner capital" value={fmt(bs.equity.owner_capital)} indent />
          <Row label="Total equity" value={fmt(bs.equity.total)} bold />
        </tbody>
      </table>
    </div>
  </div>
);

export default AdminReports;
